const EMAIL_WRITER_POLLING_SESSION_PREFIX = "email-writer-polling:";

export const EMAIL_WRITER_POLLING_SESSION_CHANGED = "email-writer-polling-session-changed";

/**
 * Remembers, per browser tab, which writer jobs this user started so their completion can be
 * announced. Job type and status come from the server; this only answers "who asked to be told".
 */
export type EmailWriterPollingSession = {
  clientId: string;
  /** Unset watches every writing job in the campaign — only a campaign just created by the writer. */
  touchpointIds?: string[];
  startedAt: number;
};

const getSessionKey = (campaignId: string) => `${EMAIL_WRITER_POLLING_SESSION_PREFIX}${campaignId}`;

const notifySessionChanged = () => {
  window.dispatchEvent(new Event(EMAIL_WRITER_POLLING_SESSION_CHANGED));
};

export const getEmailWriterPollingSession = (campaignId: string): EmailWriterPollingSession | null => {
  try {
    const session = JSON.parse(sessionStorage.getItem(getSessionKey(campaignId)) ?? "null");

    return typeof session?.clientId === "string" && typeof session?.startedAt === "number"
      ? session
      : null;
  } catch {
    return null;
  }
};

/** Merges into a session already running for the campaign, so the earlier jobs stay watched. */
export const markEmailWriterPollingStarted = (
  campaignId: string,
  clientId: string,
  touchpointIds?: string[],
) => {
  const existing = getEmailWriterPollingSession(campaignId);
  const isCampaignWide = existing !== null && !existing.touchpointIds;
  const session: EmailWriterPollingSession = {
    clientId,
    touchpointIds:
      !touchpointIds || isCampaignWide
        ? undefined
        : Array.from(new Set([...(existing?.touchpointIds ?? []), ...touchpointIds])),
    startedAt: Date.now(),
  };

  sessionStorage.setItem(getSessionKey(campaignId), JSON.stringify(session));
  notifySessionChanged();
};

export const clearEmailWriterPollingSession = (campaignId: string) => {
  sessionStorage.removeItem(getSessionKey(campaignId));
  notifySessionChanged();
};

export const getEmailWriterPollingCampaignIds = (clientId: string): string[] =>
  Array.from({ length: sessionStorage.length }, (_, index) => sessionStorage.key(index) ?? "")
    .filter((key) => key.startsWith(EMAIL_WRITER_POLLING_SESSION_PREFIX))
    .map((key) => key.slice(EMAIL_WRITER_POLLING_SESSION_PREFIX.length))
    .filter((campaignId) => getEmailWriterPollingSession(campaignId)?.clientId === clientId);
